import React from 'react';
import { Box, Typography, Chip, Paper, useTheme } from '@mui/material';
import { LabeledChip } from '@/components/common';
import { QUESTION_TYPE_LABELS } from '@/components/campaigns/CampaignQuestionsEditor/constants';
import { convertFormulaToDisplay } from '@/components/campaigns/FormulaBuilder/formulaUtils';
import { getQuestionDisplayTitle, hasRatingScale, isQualityScoreFormula } from '@/components/campaigns/CampaignQuestionsEditor/utils';
import { RATING_SCALE_LABEL } from '@/components/campaigns/CampaignQuestionsEditor/constants';
import QuestionTypeIcon from '@/components/calls/CallInsights/QuestionTypeIcon';

import { QuestionType, type CampaignQuestion } from '../types';

interface QuestionViewProps {
    question: CampaignQuestion;
    index?: number;
    allQuestions?: CampaignQuestion[];
}

/**
 * Renders a question in view mode.
 *
 * @param {QuestionViewProps} props
 * @returns {JSX.Element}
 */
const QuestionView: React.FC<QuestionViewProps> = ({ question, index = 0, allQuestions = [] }) => {
    const theme = useTheme();
    const isFormula = question.type === QuestionType.FORMULA;
    const isMultiSelect = question.type === QuestionType.MULTISELECT;

    const finalTitle = getQuestionDisplayTitle(question, index);

    // Convert UUID-based formula to display format
    const questionFormula = isFormula && question.formula ? convertFormulaToDisplay(question.formula, allQuestions) : '';

    const questionRatingScale = hasRatingScale(question) ? RATING_SCALE_LABEL : '';

    return (
        <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: theme.palette.gray[10] }} data-testid={`question-${index}`}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="h5">{finalTitle}</Typography>
                <Chip
                    size="small"
                    icon={<QuestionTypeIcon type={question.type} useRatingScale={hasRatingScale(question)} />}
                    label={QUESTION_TYPE_LABELS[question.type]}
                    sx={{ px: 0.5 }}
                />
            </Box>
            <Box>
                {!isFormula && <Typography variant="body2">{question.text || 'No question text provided'}</Typography>}
                {isMultiSelect && question.options.length > 0 && <LabeledChip label="Options" value={question.options} sx={{ mt: 0.75 }} />}
                {questionRatingScale && <LabeledChip label="Rating scale enabled" value={questionRatingScale} sx={{ mt: 0.75 }} />}
                {questionFormula && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                        <LabeledChip label="Formula" value={questionFormula} sx={{ mt: 0 }} />
                        {isQualityScoreFormula(question) && <Chip size="small" label="Quality score" variant="outlined" />}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default QuestionView;
